/**
 * StatusBanner Component
 * Sprint 28: Displays contextual warnings/errors for rider status issues
 * 
 * Shows banners for:
 * - Rider suspended
 * - Rider inactive
 * - Zone inactive
 * - Assignment disabled
 */

import { AlertTriangle, XCircle, Clock, MapPin } from 'lucide-react';

export type BannerType = 
  | 'suspended'
  | 'inactive'
  | 'zone_inactive'
  | 'assignment_disabled'
  | 'pending_approval'
  | 'no_zone';

interface StatusBannerProps {
  type: BannerType;
  message?: string;
  onAction?: () => void;
  actionLabel?: string;
}

const bannerConfig: Record<BannerType, {
  icon: React.ReactNode;
  title: string;
  defaultMessage: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  suspended: {
    icon: <XCircle className="w-5 h-5" />,
    title: 'Account Suspended',
    defaultMessage: 'Your account has been suspended. Please contact support for assistance.',
    bgColor: 'bg-red-50',
    textColor: 'text-red-800',
    borderColor: 'border-red-200',
  },
  inactive: {
    icon: <Clock className="w-5 h-5" />,
    title: 'You are Offline',
    defaultMessage: 'Go online to start receiving orders.',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
  },
  zone_inactive: {
    icon: <MapPin className="w-5 h-5" />,
    title: 'Zone Inactive',
    defaultMessage: 'Your zone is currently closed. You cannot receive orders until it reopens.',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-800',
    borderColor: 'border-amber-200',
  },
  assignment_disabled: {
    icon: <AlertTriangle className="w-5 h-5" />,
    title: 'Assignments Paused',
    defaultMessage: 'Order assignments are temporarily paused by the system.',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-800',
    borderColor: 'border-orange-200',
  },
  pending_approval: {
    icon: <Clock className="w-5 h-5" />,
    title: 'Pending Approval',
    defaultMessage: 'Your account is pending approval. You will be notified once approved.',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-200',
  },
  no_zone: {
    icon: <MapPin className="w-5 h-5" />,
    title: 'No Zone Assigned',
    defaultMessage: 'You have not been assigned a zone yet. Please contact support.',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-800',
    borderColor: 'border-amber-200',
  },
};

export default function StatusBanner({ 
  type, 
  message, 
  onAction, 
  actionLabel 
}: StatusBannerProps) {
  const config = bannerConfig[type];

  return (
    <div className={`${config.bgColor} ${config.textColor} ${config.borderColor} border rounded-lg p-4`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm">
            {config.title}
          </h4>
          <p className="text-sm mt-1 opacity-90">
            {message || config.defaultMessage}
          </p>
          {onAction && actionLabel && (
            <button
              onClick={onAction}
              className="mt-2 text-sm font-medium underline hover:no-underline"
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Helper to determine which banner to show based on rider state
 */
export function getRiderBannerType(params: {
  riderStatus?: string;
  zoneActive?: boolean;
  hasHomeZone?: boolean;
}): BannerType | null {
  const { riderStatus, zoneActive, hasHomeZone } = params;

  // Priority order: suspended > pending > no_zone > zone_inactive > inactive

  if (riderStatus === 'suspended') {
    return 'suspended';
  }

  if (riderStatus === 'pending') {
    return 'pending_approval';
  }

  if (!hasHomeZone) {
    return 'no_zone';
  }

  if (zoneActive === false) {
    return 'zone_inactive';
  }

  // No banner needed for active riders
  return null;
}
